import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

import type { JobApplication } from '@restworld/utility-types';

import { useWorkerJobPositionsService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { useAnalytics } from '@restworld/utils-common';

import { ModalConfirmContent } from '@restworld/ui-ds-base';
import { getRestaurantPopupInfoAtom } from '../use-show-spontaneous-application';
import { useApplyJob } from '../../../hooks/use-apply-job';
import { useJobPositionDetails, useJobPositionId } from '../../../hooks';
import { readApplyStatus, type ApplyStatusOutcome } from '../../../hooks/use-job-apply-status';
import { workerIdAtom } from '../worker-entry-point.atoms';
import { entryPointModalVisibleAtom } from '../worker-entry-point-modal';
import { jobPositionApiDataAtom, jobPositionApplyStatusAtom } from '../../job-position/job-position.atoms';
import {
  workerManagedFooterMapKeyAtom,
  workerManagedUsingKlaaryoModalFlagAtom,
} from '../../worker-managed-using-klaaryo';
import { CardPrimary } from '../../legacy/card';
import { Timeline } from '../../legacy';
import { ApplyConfirmation } from '../../legacy';
import { InAppAssessmentReturningConfirmation } from './in-app-assessment-confirmation';
import { reportAssessmentChannelMissing } from './in-app-assessment-observability';

const useTimeLineSteps = () => {
  const { t } = useTranslation('onboarding-page');

  return useMemo(
    () => [
      {
        title: t('application_received'),
        checked: true,
      },
      {
        key: 2,
        title: t('interview_with_our_manager'),
      },
      {
        key: 3,
        title: t('application_sent_to_the_company'),
      },
      {
        key: 4,
        title: t('contact_with_the_company'),
      },
      {
        key: 5,
        title: t('on_site_interview'),
      },
      {
        key: 6,
        title: t('trial_period'),
      },
      {
        isFlagged: true,
        title: t('assumption'),
      },
    ],
    [t]
  );
};
export const JobApplyConfirmation = () => {
  const [, toggleEntryPoint] = useAtom(entryPointModalVisibleAtom);
  const closeEntryPoint = () => toggleEntryPoint(false);
  const [, setJobPositionApplyStatus] = useAtom(jobPositionApplyStatusAtom);
  const jobPositionApiData = useAtomValue(jobPositionApiDataAtom);
  const jobPositionData = useJobPositionDetails({ jobPositionApiData });
  const router = useRouter();
  const [showSuccessView, toggleSuccessView] = useState(false);
  /**
   * The application as the apply request handed it back. Its `assessment_channel` is the only
   * thing that decides between the interview confirmation and the one this modal has always
   * shown; absent or null (every application created before the in-app rollout) means "not in
   * app", so the default is today's screen rather than the new one.
   */
  const [createdApplication, setCreatedApplication] = useState<Partial<JobApplication> | undefined>(undefined);
  /** Set by `onAuthenticated`, which is the only signal that the application was really created. */
  const appliedOkRef = useRef(false);
  const workerId = useAtomValue(workerIdAtom);
  const jobPositionId = useJobPositionId();
  const { user } = useAuth();
  const setWorkerManagedUsingKlaaryoModalFlag = useSetAtom(workerManagedUsingKlaaryoModalFlagAtom);
  const setWorkerManagedFooterMapKey = useSetAtom(workerManagedFooterMapKeyAtom);

  const manageUsingKlaaryo = jobPositionApiData?.manage_using_klaaryo;
  const isAtsWithApplyUrl = !!jobPositionApiData?.ats_applyurl;

  const { applyJobPosition, jobPositionLoading } = useApplyJob({
    referralCode: router?.query?.['scout'],
    onAuthenticated: () => {
      if (setJobPositionApplyStatus) setJobPositionApplyStatus(true);
      // **Only the ATS case is decided here**, and it is decided here because it is not a
      // confirmation at all: the worker has to continue on someone else's site, and that
      // redirect does not depend on where their assessment runs.
      //
      // `manage_using_klaaryo` used to be decided here too, and that was the defect measured
      // on stage on 21/09 (decision 136): this callback runs *before* the created application
      // is in hand, so it took the screen away and `appliedOkRef` was never set, which made
      // `onProceedWithApplication` return early and the channel gate below it unreachable. A
      // worker whose interview runs in the app was shown the card that promises Serena will
      // write to them on WhatsApp.
      //
      // It is not a corner: on production over the thirty days to 22/09, 258.692 applications
      // out of 258.814 were on positions with `manage_using_klaaryo` set, 494 positions out of
      // 495. Deciding here meant the in-app confirmations could reach exactly one position.
      if (isAtsWithApplyUrl) {
        setWorkerManagedUsingKlaaryoModalFlag(true);
        setWorkerManagedFooterMapKey('direct_applied');
        closeEntryPoint();
      } else {
        // The success view is flipped by `onProceedWithApplication` below instead, once the
        // created application is in hand: which confirmation to draw depends on its
        // `assessment_channel`, and flipping here would show today's one for a frame first.
        appliedOkRef.current = true;
      }
    },
  });
  const { t } = useTranslation('onboarding-page');
  const restaurantPopupInfo = useAtomValue(getRestaurantPopupInfoAtom);
  const timeLineSteps = useTimeLineSteps();
  const { trackEvent } = useAnalytics();
  const workerJobPositionsService = useWorkerJobPositionsService();

  // **Does this worker already have an application here?** Asked when the confirmation opens,
  // for this position, before the button can be pressed.
  //
  // 05/10/2026: 1,079 POST /apply answered 400 in 202 sessions since 01/10. 142 of them are the
  // same path: an ambassador link, "Candidati" while logged out, login, back on the position with
  // `?apply=true`, and this confirmation opened by that intent without looking at the
  // application, for a worker who already had one (shortlisted, viewed, discarded). The backend
  // refuses, and the button did nothing: one session pressed it 162 times in 29 minutes. The
  // "already applied" modal that exists could not help, because it is suppressed while this
  // panel is open. Decided here, with an explicit outcome carrying its position id, so an answer
  // for another position (a client navigation) is never applied to this one.
  // The position the check has answered for, not a boolean: after a client navigation to another
  // position the first render must already count as unchecked (Codex review, 05/10/2026).
  const [checkedFor, setCheckedFor] = useState<string | undefined>(undefined);
  const statusChecked = checkedFor !== undefined && checkedFor === jobPositionId;
  const [applyError, setApplyError] = useState<string | undefined>(undefined);
  const currentJobPositionId = useRef(jobPositionId);
  currentJobPositionId.current = jobPositionId;
  // The check must run once per position and worker, not once per render: the service, the
  // router and the analytics are read through refs so their identities cannot re-trigger it.
  const latest = useRef({ workerJobPositionsService, router, trackEvent, toggleEntryPoint });
  latest.current = { workerJobPositionsService, router, trackEvent, toggleEntryPoint };
  const actOnStatus = useCallback((outcome: ApplyStatusOutcome): boolean => {
    if (outcome.jobPositionId !== currentJobPositionId.current) return true;
    const { router: nav, trackEvent: track, toggleEntryPoint: toggle } = latest.current;
    if (outcome.kind === 'already_applied') {
      track('apply_confirmation_skipped', { job_position_id: outcome.jobPositionId, reason: 'already_applied' });
      toggle(false);
      void nav.push(`/candidature/${outcome.jobPositionId}`);
      return true;
    }
    if (outcome.kind === 'proposed') {
      // The position page already shows the interest actions for a proposal, and the backend
      // refuses an application over one (job_application.ex:584-597): nothing to confirm.
      track('apply_confirmation_skipped', { job_position_id: outcome.jobPositionId, reason: 'proposed' });
      toggle(false);
      return true;
    }
    return false;
  }, []);
  const checkingWorkerId = user?.id || workerId;
  useEffect(() => {
    if (!jobPositionId) return;
    let alive = true;
    void readApplyStatus(latest.current.workerJobPositionsService, checkingWorkerId, jobPositionId).then((outcome) => {
      if (!alive) return;
      // `not_applied` and `failed` keep today's confirmation: a check that fails must not take
      // the application away from someone who wants to apply.
      if (!actOnStatus(outcome)) setCheckedFor(outcome.jobPositionId);
    });
    return () => {
      alive = false;
    };
  }, [jobPositionId, checkingWorkerId, actOnStatus]);

  // Which confirmation the worker actually saw. The application is already counted by
  // `worker_applies_for_job`; what was missing is what happened next, and the three screens
  // that follow an application are not interchangeable: the in-app one hands the worker to
  // Serena in the same tab, the Klaaryo one sends them to WhatsApp, the plain one ends the
  // flow. Without this, a drop in interviews started cannot be told apart from a drop in
  // applications that ever reached the interview screen.
  //
  // `trackEvent` is in the deps on purpose: its identity changes when PostHog finishes
  // loading, and an on-mount capture that does not depend on it is dropped in production
  // (docs/claude/analytics.md).
  const confirmationChannel = createdApplication?.assessment_channel;
  useEffect(() => {
    if (!showSuccessView) return;
    trackEvent('worker_apply_confirmation_shown', {
      job_position_id: jobPositionId,
      assessment_channel: confirmationChannel ?? null,
      screen: confirmationChannel === 'in_app' ? 'in_app_assessment' : 'application_sent',
    });
  }, [showSuccessView, confirmationChannel, jobPositionId, trackEvent]);

  const onProceedWithApplication = useCallback(async () => {
    appliedOkRef.current = false;
    setApplyError(undefined);
    const created = await applyJobPosition({
      workerIdArg: user?.id || workerId,
      jobPositionId,
      source: 'job_position_page',
    });
    // `applyJobPosition` swallows the error and returns it, so the returned value alone cannot
    // tell success from failure. A failure used to change nothing on screen, whatever the error:
    // now the screen always changes, towards the application or towards an error line.
    if (!appliedOkRef.current) {
      const failed = created as { code?: unknown; response?: { status?: number } } | undefined;
      const status = typeof failed?.code === 'number' ? failed.code : failed?.response?.status;
      // A 400 is what the backend answers to an application that already exists in another
      // status: read the application again and act on it like at opening.
      const outcome =
        status === 400 ? await readApplyStatus(workerJobPositionsService, user?.id || workerId, jobPositionId) : null;
      trackEvent('worker_apply_rejected', {
        job_position_id: jobPositionId,
        status: status ?? null,
        outcome: outcome?.kind ?? null,
      });
      if (outcome && actOnStatus(outcome)) return;
      setApplyError(t('not_able_to_process_try_again_later.', { ns: 'common' }));
      return;
    }
    const application = (created as { job_application?: Partial<JobApplication> })?.job_application;
    // Reported, not acted on: the fallback below is the right outcome either way. It is here
    // because a gate that silently never opens looks exactly like a gate that is working.
    const channel = application?.assessment_channel;
    if (channel !== 'in_app' && channel !== 'whatsapp')
      reportAssessmentChannelMissing({ channel, surface: 'job_apply_confirmation', jobPositionId });
    // The Klaaryo screen, now that the channel is known and can be respected. Unchanged for
    // every worker whose assessment does not run in the app, footer key included: they see
    // what they saw before, one await later.
    if (manageUsingKlaaryo && channel !== 'in_app') {
      setWorkerManagedUsingKlaaryoModalFlag(true);
      setWorkerManagedFooterMapKey('direct_applied');
      closeEntryPoint();
      return;
    }

    setCreatedApplication(application);
    toggleSuccessView(true);
  }, [
    applyJobPosition,
    user?.id,
    workerId,
    jobPositionId,
    manageUsingKlaaryo,
    setWorkerManagedUsingKlaaryoModalFlag,
    setWorkerManagedFooterMapKey,
    closeEntryPoint,
    workerJobPositionsService,
    trackEvent,
    actOnStatus,
    t,
  ]);

  // The gate, and the only one: the new confirmations belong to an application whose
  // assessment runs in the app. Anything else, a missing or null field included, keeps the
  // screen this modal has always shown.
  if (showSuccessView && createdApplication?.assessment_channel === 'in_app')
    return <InAppAssessmentReturningConfirmation jobPositionId={jobPositionId} />;

  return showSuccessView ? (
    <ModalConfirmContent
      title={t('your_application_has_been_sent')}
      subtitle={t('your_application_has_been_sent')}
      primaryAction={{
        label: t('view_your_applications'),
        onClick: () => {
          trackEvent('worker_cta_clicked', { cta: 'view_applications', surface: 'apply_confirmation' });
          router.push(`/candidature/${jobPositionId}`);
        },
      }}
      secondaryAction={{
        label: t('find_more_jobs'),
        onClick: () => {
          trackEvent('worker_cta_clicked', { cta: 'find_more_jobs', surface: 'apply_confirmation' });
          router.push(`/cerco-lavoro`);
        },
      }}
    >
      <div className="flex flex-col items-center relative">
        <Timeline timeLineSteps={timeLineSteps} />
      </div>
    </ModalConfirmContent>
  ) : (
    <ApplyConfirmation
      onProceedWithApplication={onProceedWithApplication}
      onChangeMind={() => (closeEntryPoint ? closeEntryPoint() : null)}
      proceedButtonTitle={t('apply_now')}
      exitButtonTitle={t('changed_my_mind')}
      mainTitle={t('confirm_that_you_want_to_apply')}
      // Loading until the application has been checked: the button cannot be pressed for a
      // worker who turns out to be applied already.
      loading={jobPositionLoading || !statusChecked}
      restaurantPopupInfo={restaurantPopupInfo}
      errorMessage={applyError}
    >
      {jobPositionData ? <CardPrimary {...jobPositionData} variant="gradientBase" twStyle={'max-w-xs'} /> : null}
    </ApplyConfirmation>
  );
};
